import { useEffect, useRef, useState } from "react";
import type { DashboardStats } from "@/hooks/useDashboardStats";

interface StatsCardsProps {
  stats: DashboardStats;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

function computeChange(current: number, previous: number): { change: string; changeType: "up" | "down" | "zero" } {
  if (previous === 0 && current === 0) return { change: "0%", changeType: "zero" };
  if (previous === 0) return { change: "+100%", changeType: "up" };
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return { change: `+${pct}%`, changeType: "up" };
  if (pct < 0) return { change: `${pct}%`, changeType: "down" };
  return { change: "0%", changeType: "zero" };
}

const colorMap: Record<string, { bg: string; icon: string; cardBg: string }> = {
  primary: { bg: "bg-primary-100", icon: "text-primary-600", cardBg: "bg-primary-50/60" },
  accent: { bg: "bg-accent-100", icon: "text-accent-600", cardBg: "bg-accent-50/60" },
  secondary: { bg: "bg-secondary-100", icon: "text-secondary-600", cardBg: "bg-secondary-50/60" },
};

function SkeletonCard() {
  return (
    <div className="bg-primary-50/60 rounded-xl p-5 border border-background-200/70 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 bg-background-200 rounded-xl"></div>
        <div className="w-14 h-5 bg-background-200 rounded-full"></div>
      </div>
      <div className="h-8 bg-background-200 rounded-md w-2/3 mb-1"></div>
      <div className="h-4 bg-background-200 rounded-md w-1/2"></div>
    </div>
  );
}

function useChangedKeys(stats: DashboardStats): Set<string> {
  const prevRef = useRef<DashboardStats | null>(null);
  const [pulsedKeys, setPulsedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const prev = prevRef.current;
    if (prev && !isInitialMount.current) {
      const changed = new Set<string>();

      if (prev.activeJobs !== stats.activeJobs) changed.add("activeJobs");
      if (prev.totalApplications !== stats.totalApplications) changed.add("totalApplications");
      if (prev.newApplicationsThisMonth !== stats.newApplicationsThisMonth) changed.add("newApplications");
      if (prev.conversionRate !== stats.conversionRate || prev.hiredCount !== stats.hiredCount) changed.add("conversionRate");

      if (changed.size > 0) {
        setPulsedKeys(changed);
        const timer = setTimeout(() => setPulsedKeys(new Set()), 700);
        return () => clearTimeout(timer);
      }
    }

    prevRef.current = stats;
  }, [stats]);

  return pulsedKeys;
}

const isInitialMount = { current: true };

export default function StatsCards({ stats, loading, error, onRetry }: StatsCardsProps) {
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  const pulsedKeys = useChangedKeys(stats);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <i className="ri-error-warning-line text-red-500 text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-700">Không thể tải thống kê</p>
          <p className="text-xs text-red-500 mt-0.5">{error}</p>
        </div>
        <button
          onClick={onRetry}
          className="px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          Thử lại
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const appChange = computeChange(stats.totalApplications, stats.lastMonthApplications);
  const hireChange = computeChange(stats.hiredCount, stats.lastMonthHires);

  const cards = [
    {
      key: "activeJobs",
      label: "Tin đang tuyển",
      value: stats.activeJobs,
      total: stats.totalJobs,
      icon: "ri-briefcase-line",
      change: undefined,
      changeType: undefined,
      color: "primary",
    },
    {
      key: "totalApplications",
      label: "Tổng ứng viên",
      value: stats.totalApplications,
      icon: "ri-user-line",
      change: appChange.change,
      changeType: appChange.changeType,
      color: "accent",
    },
    {
      key: "newApplications",
      label: "Ứng viên mới tháng này",
      value: stats.newApplicationsThisMonth,
      icon: "ri-user-add-line",
      change: appChange.change,
      changeType: appChange.changeType,
      color: "secondary",
    },
    {
      key: "conversionRate",
      label: "Tỷ lệ chuyển đổi",
      value: `${stats.conversionRate}%`,
      icon: "ri-line-chart-line",
      change: hireChange.change,
      changeType: hireChange.changeType,
      color: "primary",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => {
        const c = colorMap[card.color];
        const isUp = card.changeType === "up";
        const hasChange = card.changeType && card.changeType !== "zero";
        const isPulsed = pulsedKeys.has(card.key);

        return (
          <div
            key={card.key}
            className={`${c.cardBg} rounded-xl p-5 border transition-all hover:border-background-300/60 ${
              isPulsed ? "border-accent-300/60" : "border-background-200/70"
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center`}>
                <i className={`${card.icon} text-lg ${c.icon}`}></i>
              </div>
              {hasChange && (
                <span
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                    isUp ? "bg-accent-100 text-accent-700" : "bg-red-100 text-red-600"
                  }`}
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`${isUp ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-xs`}></i>
                  </div>
                  {card.change}
                </span>
              )}
            </div>
            <p
              className={`text-2xl md:text-3xl font-bold font-heading whitespace-nowrap ${
                isPulsed ? "text-accent-600 animate-stat-pulse" : "text-foreground-950"
              }`}
            >
              {typeof card.value === "number" ? card.value.toLocaleString("vi-VN") : card.value}
            </p>
            <p className="text-sm text-foreground-600 mt-1">{card.label}</p>
            {card.total !== undefined && (
              <p className="text-[11px] text-foreground-400 mt-1">
                trên tổng số {card.total} tin
              </p>
            )}
            {card.key === "conversionRate" && stats.hiredCount > 0 && (
              <p className="text-[11px] text-foreground-400 mt-1">
                {stats.hiredCount} đã tuyển / {stats.totalApplications} ứng viên
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}